import {
  View,
  TouchableOpacity,
  KeyboardAvoidingView,
  ScrollView,
  Platform,
} from "react-native"
import { type Dispatch, type SetStateAction, useState } from "react"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { router } from "expo-router"
import { PrimaryButton } from "@/components/buttons/CommonButtons"
import { showErrorToast, showSuccessToast } from "@/components/toasts/AppToast"
import * as FeedbackEvents from "@/analytics/events/feedback"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import { H2, Body } from "@/components/standard/BFText"
import { BFTextInput } from "@/components/standard/BFTextInput"
import CloseButton from "@/assets/icons/close-button.svg"
import { sendFeedbackToSentry } from "@/utils/SentryUtils"
import { useUser } from "@/queries/user"
import { type UserType } from "@/generated/graphql"
import AgreeCheckBox from "@/components/forms/AgreeCheckBox"

export default function QualitativeFeedbackScreen() {
  const insets = useSafeAreaInsets()
  const { data, isLoading } = useUser()
  const userProfile = data?.me

  useViewedCallback(() => {
    setStatusBarStyle("dark")
    FeedbackEvents.feedbackFormViewed()
  })

  const [feedbackMessage, setFeedbackMessage] = useState<string | undefined>(
    undefined
  )

  const [contactSelected, setContactSelected] = useState<boolean>(false)

  const isDisabled = isLoading || typeof feedbackMessage === "undefined"

  const onSubmit = () => {
    FeedbackEvents.feedbackSubmitClicked({
      message: feedbackMessage || "N/A",
      canContact: contactSelected,
    })

    if (!feedbackMessage) {
      showErrorToast({ message: "Feedback should not be blank." })
      return
    }

    if (!userProfile) {
      showErrorToast({
        message: "Unable to report feedback. User object unavailable.",
      })
      return
    }

    sendFeedbackToSentry(
      userProfile as UserType,
      feedbackMessage,
      contactSelected
    )
      .then((successMessage) => {
        showSuccessToast({ message: successMessage, autoHide: true })
        router.push("../")
      })
      .catch((error) => {
        showErrorToast({ message: `${error}.\nUnable to report feedback.` })
      })
  }

  return (
    <KeyboardAvoidingView
      className="flex-col justify-start flex-1 bg-off-white"
      behavior="padding"
      style={{
        paddingTop: insets.top + 10,
      }}
    >
      <View className="flex flex-col px-8 shrink-0">
        <View
          className="flex items-end justify-center"
          style={{
            height: Platform.OS === "web" ? 20 : 70,
          }}
        >
          <TouchableOpacity
            testID={"close-button"}
            hitSlop={32}
            onPress={() => {
              router.push("../")
            }}
          >
            <CloseButton width={13} height={13} />
          </TouchableOpacity>
        </View>
        <H2 className="mt-1">Help us improve!</H2>
      </View>
      <View className="flex flex-col justify-between flex-1 h-full px-8 pb-8">
        <QualitativeFeedbackForm
          contactSelected={contactSelected}
          setContactSelected={setContactSelected}
          feedbackMessage={feedbackMessage}
          setFeedbackMessage={setFeedbackMessage}
        />

        <View className="flex flex-col flex-1 max-h-14">
          <PrimaryButton
            testID={"submit-button"}
            title="Submit"
            onPress={onSubmit}
            disabled={isDisabled}
          />
        </View>
      </View>
    </KeyboardAvoidingView>
  )
}

interface QualitativeFeedbackFormProps {
  contactSelected: boolean
  setContactSelected: Dispatch<SetStateAction<boolean>>
  feedbackMessage: string | undefined
  setFeedbackMessage: Dispatch<SetStateAction<string | undefined>>
}

const QualitativeFeedbackForm: React.FC<QualitativeFeedbackFormProps> = ({
  contactSelected,
  setContactSelected,
  feedbackMessage,
  setFeedbackMessage,
}: QualitativeFeedbackFormProps) => {
  return (
    <ScrollView
      className="flex flex-col flex-1 pb-20"
      contentContainerClassName="flex-grow flex flex-col justify-start"
    >
      <Body className="mt-5">
        While our team works on enhancing this page with more market data,
        we&apos;d love to hear from you. What real estate market insights would
        you like to see?
      </Body>

      <View className="flex flex-col flex-1 bg-lightest max-h-[330px]">
        <View className="flex-1 p-4 mt-3 border rounded min-h-56 border-warm-grey">
          <BFTextInput
            multiline
            placeholder="I was looking for..."
            value={feedbackMessage}
            onChangeText={(value) => {
              setFeedbackMessage(value)
            }}
            editable={true}
            enterKeyHint="done"
            style={{ textAlignVertical: "top" }} // Necessary for Android ¯\_(ツ)_/¯
            className="size-full bg-white font-spacegrotesk placeholder:text-warm-grey placeholder:font-spacegrotesk"
          />
        </View>

        <View className="mt-3">
          <AgreeCheckBox
            text="Yes! You may reach out to gather more feedback."
            checked={contactSelected}
            onPress={() => {
              setContactSelected(!contactSelected)
            }}
          />
        </View>
      </View>
    </ScrollView>
  )
}
